import { useEffect, useState } from 'react';

import { ExternalLink, FileSignature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSignaturesPendingCount } from '@/hooks/useSignatures';
import { useAuthStore } from '@/stores/auth.store';
import { useFeaturesStore } from '@/stores/features.store';

const STORAGE_KEY = 'signatures-alert-last-shown';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Once-a-day reminder that documents are waiting to be signed. Shown on the
 * first page load of each day (tracked in localStorage) and only while
 * something is actually pending — so it stops by itself once everything is
 * signed, without needing to be dismissed permanently.
 */
export const PendingSignaturesAlert = () => {
  const navigate = useNavigate();
  const digitalSign = useFeaturesStore((s) => s.digitalSign);
  const featuresLoaded = useFeaturesStore((s) => s.isLoaded);
  // Clients view: the signatures endpoint is off-limits to 'client' users —
  // polling it just raises a permission error toast on every page.
  const isClientView = useAuthStore((s) => s.user?.role === 'client');

  const { data } = useSignaturesPendingCount(digitalSign && featuresLoaded && !isClientView);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!data || data.count === 0) return;
    let lastShown: string | null = null;
    try {
      lastShown = localStorage.getItem(STORAGE_KEY);
    } catch {
      // Private mode / storage disabled — fall through and show the reminder.
    }
    if (lastShown === today()) return;
    const id = window.setTimeout(() => setOpen(true), 0);
    try {
      localStorage.setItem(STORAGE_KEY, today());
    } catch {
      // Not persistable; the reminder simply shows again next load.
    }
    return () => window.clearTimeout(id);
  }, [data]);

  if (!data || data.count === 0) return null;

  const goToSignatures = () => {
    setOpen(false);
    navigate('/signatures');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
              <FileSignature className="h-4 w-4" />
            </span>
            {data.count === 1
              ? '1 document is awaiting your signature'
              : `${data.count} documents are awaiting your signature`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Please review and sign so we can proceed. You can also open them any time from the
            Signatures page.
          </p>

          <ul className="space-y-2">
            {data.items.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 dark:border-slate-700"
              >
                <span className="truncate text-sm font-medium">{item.title}</span>
                {item.signing_url && (
                  <a
                    href={item.signing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex shrink-0 items-center gap-1 text-xs hover:underline"
                  >
                    Sign now
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </li>
            ))}
          </ul>

          {data.count > data.items.length && (
            <p className="text-muted-foreground text-xs">
              …and {data.count - data.items.length} more.
            </p>
          )}

          <div className="flex justify-end gap-2 border-t pt-3 dark:border-slate-700">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Remind me tomorrow
            </Button>
            <Button size="sm" onClick={goToSignatures}>
              View documents
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
